import { getApiGatewayUrl } from '@trello/api-gateway';
import { Analytics } from '@trello/atlassian-analytics';
import { clientVersion } from '@trello/config';
import { sendErrorEvent } from '@trello/error-reporting';
import { fetch } from '@trello/fetch';

import { CloudInstanceRestrictedError } from './CloudInstanceRestrictedError';
import { jiraApiFetch } from './jiraApiFetch';

export const isInstanceMember = async (idCloud: string): Promise<boolean> => {
  const url = getApiGatewayUrl('permissions/permitted');

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': clientVersion,
      },
      body: JSON.stringify({
        permissionId: 'write',
        resourceId: `ari:cloud:jira::site/${idCloud}`,
      }),
    });

    if (response.ok) {
      return (await response.json()).permitted;
    }

    if (response.status === 401) {
      // There is a known issue where logged in Trello users can
      // have a missing or invalid Atlassian auth cookie token.
      Analytics.sendOperationalEvent({
        action: 'attempted',
        actionSubject: 'fetchPermissionsPermitted',
        source: '@trello/jwm',
      });
    } else {
      sendErrorEvent(new Error('Error response from permissions/permitted'), {
        tags: {
          feature: 'JWM Workspace Linking',
          ownershipArea: 'trello-cross-flow-b',
        },
        extraData: {
          statusCode: String(response.status),
          statusText: response.statusText,
          component: 'isInstanceMember',
        },
      });
    }

    return false;
  } catch (error) {
    sendErrorEvent(error, {
      tags: {
        feature: 'JWM Workspace Linking',
        ownershipArea: 'trello-cross-flow-b',
      },
      extraData: {
        component: 'isInstanceMember',
      },
    });
    return false;
  }
};

export const isSiteAdmin = async (idCloud: string): Promise<boolean> => {
  const url = getApiGatewayUrl('permissions/permitted');

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Trello-Client-Version': clientVersion,
      },
      body: JSON.stringify({
        permissionId: 'manage',
        resourceId: `ari:cloud:platform::site/${idCloud}`,
      }),
    });

    if (response.ok) {
      return (await response.json()).permitted;
    }

    if (response.status === 401) {
      // There is a known issue where logged in Trello users can
      // have a missing or invalid Atlassian auth cookie token.
      Analytics.sendOperationalEvent({
        action: 'attempted',
        actionSubject: 'fetchPermissionsPermitted',
        source: '@trello/jwm',
      });
    } else {
      sendErrorEvent(new Error('Error response from permissions/permitted'), {
        tags: {
          feature: 'JWM Workspace Linking',
          ownershipArea: 'trello-cross-flow-b',
        },
        extraData: {
          statusCode: String(response.status),
          statusText: response.statusText,
          component: 'isSiteAdmin',
        },
      });
    }

    return false;
  } catch (error) {
    sendErrorEvent(error, {
      tags: {
        feature: 'JWM Workspace Linking',
        ownershipArea: 'trello-cross-flow-b',
      },
      extraData: {
        component: 'isSiteAdmin',
      },
    });
    return false;
  }
};

export const canCreateProject = async (idCloud: string): Promise<boolean> => {
  const path = `/3/mypermissions?permissions=CREATE_PROJECT%2CADMINISTER`;

  try {
    const response = await jiraApiFetch(idCloud, path);

    const { CREATE_PROJECT, ADMINISTER } = response.permissions;
    return CREATE_PROJECT.havePermission || ADMINISTER.havePermission;
  } catch (error) {
    if (!(error instanceof CloudInstanceRestrictedError)) {
      sendErrorEvent(error, {
        tags: {
          feature: 'JWM Workspace Linking',
          ownershipArea: 'trello-cross-flow-b',
        },
        extraData: {
          component: 'canCreateProject',
        },
      });
    }
    return false;
  }
};
